import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
  Stack,
  useTheme,
  alpha,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Visibility as PreviewIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  Description as FormIcon,
  Palette as BrandingIcon,
  Share as ShareIcon,
  QrCode as QrCodeIcon,
  CheckCircle as EnabledIcon,
  Cancel as DisabledIcon,
  Star as StarIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { UseApi } from '../Hooks/UseApi';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';

const InquiryFormSettings = () => {
  const theme = useTheme();
  const api = UseApi();
  const navigate = useNavigate();
  const { hasRole } = UseAuthRoles();
  const apiRef = useRef(api);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    inquiry_form_enabled: false,
    inquiry_form_slug: '',
    inquiry_logo_url: '',
    inquiry_tagline: '',
    selected_form_template_id: null,
    max_reference_images: 3,
    require_phone: false,
    require_budget: false,
    require_timeframe: false,
    notification_email: '',
    auto_reply_enabled: false,
    auto_reply_message: ''
  });
  
  // Available form templates
  const [formTemplates, setFormTemplates] = useState([]);
  
  // Logo upload state
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  
  // QR Code dialog
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  // Check if user has custom form access (Standard tier and above)
  const hasCustomFormAccess = () => {
    return hasRole('standard_subscriber') || 
           hasRole('starter_studio_subscriber') || 
           hasRole('growth_studio_subscriber') ||
           hasRole('starter_studio_member') ||
           hasRole('growth_studio_member');
  };

  // Check if user is basic tier only
  const isBasicTier = () => {
    return hasRole('basic_subscriber') && !hasCustomFormAccess();
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiRef.current.fetchWithAuth('/inquiry-form/settings/');
      
      setSettings({
        inquiry_form_enabled: response.inquiry_form_enabled || false,
        inquiry_form_slug: response.inquiry_form_slug || '',
        inquiry_logo_url: response.inquiry_logo_url || '',
        inquiry_tagline: response.inquiry_tagline || '',
        selected_form_template_id: response.selected_form_template_id || null,
        max_reference_images: response.max_reference_images || 3,
        require_phone: response.require_phone || false,
        require_budget: response.require_budget || false,
        require_timeframe: response.require_timeframe || false,
        notification_email: response.notification_email || '',
        auto_reply_enabled: response.auto_reply_enabled || false,
        auto_reply_message: response.auto_reply_message || ''
      });
      
      if (response.inquiry_logo_url) {
        setLogoPreview(response.inquiry_logo_url);
      }
      
      // Use form_templates from settings response (already filtered to intake forms only)
      if (response.form_templates && Array.isArray(response.form_templates)) {
        setFormTemplates(response.form_templates);
      }
      
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load inquiry form settings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch settings on mount - only runs once
  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo must be less than 2MB');
        return;
      }
      
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setSettings(prev => ({ ...prev, inquiry_logo_url: '' }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const formData = new FormData();
      
      // Add all settings
      Object.entries(settings).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value.toString());
        }
      });
      
      // Add logo if uploaded
      if (logoFile) {
        formData.append('inquiry_logo', logoFile);
      }
      
      await apiRef.current.fetchWithAuth('/inquiry-form/settings/', {
        method: 'POST',
        body: formData,
        headers: {} // Let browser set content-type for FormData
      });
      
      setSuccess('Inquiry form settings saved successfully!');
      setLogoFile(null); // Clear file after save
      
      // Refresh settings to get updated URLs
      await fetchSettings();
      
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getFormUrl = () => {
    const baseUrl = window.location.origin;
    const slug = settings.inquiry_form_slug;
    // Determine if studio or artist
    const type = hasRole('growth_studio_subscriber') || hasRole('starter_studio_subscriber') ? 'studio' : 'artist';
    return `${baseUrl}/request/${type}/${slug}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getFormUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleGenerateQR = () => {
    const url = getFormUrl();
    // Use a QR code API
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url)}`;
    setQrCodeUrl(qrUrl);
    setQrDialogOpen(true);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading inquiry form settings...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Inquiry Form Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure your public appointment request form that clients can access via a shareable link
        </Typography>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Settings */}
        <Grid item xs={12} lg={8}>
          {/* Enable/Disable */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Enable Public Inquiry Form
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Allow potential clients to submit appointment requests through your custom link
                </Typography>
              </Box>
              <Switch
                checked={settings.inquiry_form_enabled}
                onChange={(e) => handleSettingChange('inquiry_form_enabled', e.target.checked)}
                color="primary"
                size="medium"
              />
            </Box>
          </Paper>

          {/* Branding Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BrandingIcon color="primary" />
              Branding
            </Typography>
            
            <Grid container spacing={3}>
              {/* Logo Upload */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>
                  Form Logo
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '16/9',
                    border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }}
                >
                  {logoPreview ? (
                    <>
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        style={{
                          maxWidth: '90%',
                          maxHeight: '90%',
                          objectFit: 'contain'
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleRemoveLogo}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <label htmlFor="logo-upload" style={{ cursor: 'pointer', textAlign: 'center' }}>
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        style={{ display: 'none' }}
                      />
                      <UploadIcon sx={{ fontSize: 40, color: 'action.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Upload Logo
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Max 2MB
                      </Typography>
                    </label>
                  )}
                </Box>
              </Grid>
              
              {/* Tagline and URL */}
              <Grid item xs={12} md={8}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Form Tagline"
                    value={settings.inquiry_tagline}
                    onChange={(e) => handleSettingChange('inquiry_tagline', e.target.value)}
                    placeholder="e.g., Book your next tattoo with us"
                    helperText="Displayed below your logo on the form"
                  />
                  
                  <TextField
                    fullWidth
                    label="Form URL Slug"
                    value={settings.inquiry_form_slug}
                    onChange={(e) => handleSettingChange('inquiry_form_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="my-studio"
                    helperText="This will be part of your shareable link"
                    InputProps={{
                      startAdornment: (
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          /request/
                        </Typography>
                      )
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Custom Form Template Section - TIER AWARE */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormIcon color="primary" />
              Custom Inquiry Questions
              {!hasCustomFormAccess() && (
                <Chip 
                  icon={<StarIcon sx={{ fontSize: 16 }} />}
                  label="Standard+" 
                  size="small" 
                  color="warning"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            
            {hasCustomFormAccess() ? (
              // Standard tier and above - Show form template dropdown
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select a custom question template to add additional fields to your public inquiry form. 
                  Create templates in the Custom Forms section under the "Custom Inquiry Questions" tab.
                </Typography>
                
                <FormControl fullWidth>
                  <InputLabel>Custom Question Template</InputLabel>
                  <Select
                    value={settings.selected_form_template_id || ''}
                    onChange={(e) => handleSettingChange('selected_form_template_id', e.target.value || null)}
                    label="Custom Question Template"
                  >
                    <MenuItem value="">
                      <em>None - Use standard inquiry form only</em>
                    </MenuItem>
                    {Array.isArray(formTemplates) && formTemplates.map(template => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {(!formTemplates || formTemplates.length === 0) && (
                  <Alert 
                    severity="info" 
                    sx={{ mt: 2 }}
                    action={
                      <Button 
                        color="inherit" 
                        size="small"
                        onClick={() => navigate('/custom-forms')}
                        endIcon={<ArrowForwardIcon />}
                      >
                        Create Questions
                      </Button>
                    }
                  >
                    No custom question templates found. Create one to add custom questions to your inquiry form.
                  </Alert>
                )}
                
                {formTemplates && formTemplates.length > 0 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => navigate('/custom-forms')}
                    >
                      Manage Custom Questions
                    </Button>
                  </Box>
                )}
              </>
            ) : (
              // Basic tier - Show upgrade prompt
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <StarIcon sx={{ color: 'warning.main', fontSize: 28, mt: 0.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                      Add Custom Questions to Your Form
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      With the Standard plan and above, you can create custom form templates to collect 
                      additional information from clients, such as:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 0.5 } }}>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Skin type and sensitivity questions
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Medical history and allergies
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Style preferences (checkboxes for Traditional, Realism, etc.)
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Availability and scheduling preferences
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Any custom questions you need
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => navigate('/subscription-manager')}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ 
                        bgcolor: 'warning.main',
                        '&:hover': { bgcolor: 'warning.dark' }
                      }}
                    >
                      Upgrade to Standard - $85/mo
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Form Options */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon color="primary" />
              Form Options
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Max Reference Images</InputLabel>
                  <Select
                    value={settings.max_reference_images}
                    onChange={(e) => handleSettingChange('max_reference_images', e.target.value)}
                    label="Max Reference Images"
                  >
                    <MenuItem value={1}>1 image</MenuItem>
                    <MenuItem value={2}>2 images</MenuItem>
                    <MenuItem value={3}>3 images</MenuItem>
                    <MenuItem value={5}>5 images</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Notification Email"
                  type="email"
                  value={settings.notification_email}
                  onChange={(e) => handleSettingChange('notification_email', e.target.value)}
                  placeholder="your@email.com"
                  helperText="Where to send new inquiry notifications"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Required Fields
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.require_phone}
                        onChange={(e) => handleSettingChange('require_phone', e.target.checked)}
                        size="small"
                      />
                    }
                    label="Phone Number"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.require_budget}
                        onChange={(e) => handleSettingChange('require_budget', e.target.checked)}
                        size="small"
                      />
                    }
                    label="Budget Range"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.require_timeframe}
                        onChange={(e) => handleSettingChange('require_timeframe', e.target.checked)}
                        size="small"
                      />
                    }
                    label="Timeframe"
                  />
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Auto-Reply Settings */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Auto-Reply Email
              </Typography>
              <Switch
                checked={settings.auto_reply_enabled}
                onChange={(e) => handleSettingChange('auto_reply_enabled', e.target.checked)}
                size="small"
              />
            </Box>
            
            {settings.auto_reply_enabled && (
              <TextField
                fullWidth
                label="Auto-Reply Message"
                multiline
                rows={3}
                value={settings.auto_reply_message}
                onChange={(e) => handleSettingChange('auto_reply_message', e.target.value)}
                placeholder="Thank you for your inquiry! We'll review your request and get back to you within 24-48 hours."
                helperText="This message will be automatically sent to clients after they submit an inquiry"
              />
            )}
          </Paper>

          {/* Save Button */}
          <Button
            variant="contained"
            size="large"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            fullWidth
            sx={{ py: 1.5 }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Grid>

        {/* Sidebar - Share Options */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShareIcon color="primary" />
              Share Your Form
            </Typography>
            
            {settings.inquiry_form_enabled && settings.inquiry_form_slug ? (
              <Stack spacing={2}>
                {/* Form Status */}
                <Box 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <EnabledIcon color="success" />
                  <Typography variant="body2" color="success.dark" fontWeight="600">
                    Your inquiry form is live!
                  </Typography>
                </Box>
                
                {/* URL Display */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 2,
                    wordBreak: 'break-all'
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Your shareable link:
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {getFormUrl()}
                  </Typography>
                </Box>
                
                {/* Action Buttons */}
                <Button
                  variant="contained"
                  startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                  onClick={handleCopyLink}
                  fullWidth
                  color={copied ? 'success' : 'primary'}
                >
                  {copied ? 'Copied!' : 'Copy Link'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<QrCodeIcon />}
                  onClick={handleGenerateQR}
                  fullWidth
                >
                  Generate QR Code
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  href={getFormUrl()}
                  target="_blank"
                  fullWidth
                >
                  Preview Form
                </Button>
                
                {/* Usage Tips */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Where to share:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText 
                      primary="Instagram Bio"
                      secondary="Add link to your bio"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Website"
                      secondary="Embed or link on your site"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Business Cards"
                      secondary="Print QR code"
                    />
                  </ListItem>
                </List>
              </Stack>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <DisabledIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {!settings.inquiry_form_slug 
                    ? 'Set a URL slug to enable sharing'
                    : 'Enable your inquiry form to get a shareable link'}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>QR Code</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {qrCodeUrl && (
              <img
                src={qrCodeUrl}
                alt="QR Code"
                style={{ maxWidth: '100%', borderRadius: 8 }}
              />
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Clients can scan this code to access your inquiry form
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained"
            href={qrCodeUrl}
            download="inquiry-form-qr.png"
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InquiryFormSettings;